import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import {
  ArrowLeft, Loader2, User, Package, Layers, Edit3, Trash2, Save, X, Search, AlertCircle, CheckCircle2, Plug, ShoppingBag, Store, Tag, RefreshCw, KeyRound, Eye, EyeOff,
} from 'lucide-react';

/**
 * Admin Phase 2 — view + edit any user's products & batches without impersonating.
 * Routes:
 *   GET  /api/admin/users/:username/summary
 *   GET  /api/admin/users/:username/products
 *   PUT  /api/admin/users/:username/products/:product_id
 *   DEL  /api/admin/users/:username/products/:product_id
 *   GET  /api/admin/users/:username/batches
 *   PUT  /api/admin/users/:username/batches/:batch_id
 *   DEL  /api/admin/users/:username/batches/:batch_id
 */
export default function AdminUserDetail() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, b] = await Promise.all([
        apiCall(`/api/admin/users/${username}/summary`, { method: 'GET' }),
        apiCall(`/api/admin/users/${username}/products`, { method: 'GET' }),
        apiCall(`/api/admin/users/${username}/batches`, { method: 'GET' }),
      ]);
      setSummary(s.data || null);
      setProducts(p.data?.products || []);
      setBatches(b.data?.batches || []);
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to load user');
    } finally { setLoading(false); }
  }, [username]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredProducts = products.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.product_name || '').toLowerCase().includes(q)
      || (p.barcode || '').toLowerCase().includes(q)
      || (p.product_id || '').toLowerCase().includes(q);
  });

  const saveProduct = async () => {
    if (!editingProduct) return;
    setBusy('save-product');
    try {
      const payload = {
        product_name: editingProduct.product_name,
        selling_price: parseFloat(editingProduct.selling_price) || 0,
        retail_price: editingProduct.retail_price ?? '',
        quantity: parseInt(editingProduct.quantity) || 0,
        barcode: editingProduct.barcode || '',
      };
      await apiCall(`/api/admin/users/${username}/products/${editingProduct.product_id}`, {
        method: 'PUT', data: payload,
      });
      setEditingProduct(null);
      await fetchAll();
      showToast('ok', 'Product updated');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete product "${p.product_name}" from ${username}'s account?`)) return;
    setBusy(p.product_id);
    try {
      await apiCall(`/api/admin/users/${username}/products/${p.product_id}`, { method: 'DELETE' });
      await fetchAll();
      showToast('ok', 'Product deleted');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const saveBatch = async () => {
    if (!editingBatch) return;
    setBusy('save-batch');
    try {
      const payload = {
        batch_id: editingBatch.batch_id,
        batch_name: editingBatch.batch_name,
        batch_notes: editingBatch.batch_notes || '',
        cost_of_batch: parseFloat(editingBatch.cost_of_batch) || 0,
        selling_price: parseFloat(editingBatch.selling_price) || 0,
      };
      await apiCall(`/api/admin/users/${username}/batches/${editingBatch.batch_id}`, {
        method: 'PUT', data: payload,
      });
      setEditingBatch(null);
      await fetchAll();
      showToast('ok', 'Batch updated');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm(`Delete batch "${b.batch_name}"? Products inside will be orphaned but kept.`)) return;
    setBusy(b.batch_id);
    try {
      await apiCall(`/api/admin/users/${username}/batches/${b.batch_id}`, { method: 'DELETE' });
      await fetchAll();
      showToast('ok', 'Batch deleted');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const fetchIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try {
      const r = await apiCall(`/api/admin/users/${username}/integrations`, { method: 'GET' });
      setIntegrations(r.data || null);
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to load integrations');
    } finally { setIntegrationsLoading(false); }
  }, [username]);

  if (loading && !summary) return (
    <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" /></div>
  );

  const u = summary?.user || {};
  const sub = summary?.subscription || {};

  return (
    <div className="space-y-6" data-testid="admin-user-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => navigate('/admin-users')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0052FF]" data-testid="back-to-users">
          <ArrowLeft className="w-4 h-4" /> Back to customers
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl bg-[#0052FF]/10 flex items-center justify-center">
            <User className="w-7 h-7 text-[#0052FF]" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-xl font-bold text-[#0F172A]">{u.username}</h2>
            <p className="text-sm text-slate-500">{u.email}</p>
            <p className="text-xs text-slate-400 mt-1">{u.first_name} {u.last_name} · {u.mobile_no || 'no phone'}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Stat icon={Package} label="Products" value={summary?.product_count ?? 0} />
            <Stat icon={Layers} label="Batches" value={summary?.batch_count ?? 0} />
            <Stat icon={CheckCircle2} label="SKU Remaining" value={`${sub.remaining_sku ?? 0} / ${sub.allowed_sku ?? 0}`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[
          { id: 'products', label: `Products (${products.length})`, icon: Package },
          { id: 'batches', label: `Batches (${batches.length})`, icon: Layers },
          { id: 'integrations', label: 'Integrations', icon: Plug },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'integrations' && !integrations) fetchIntegrations(); }}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'products' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, barcode, ID..."
              className="flex-1 text-sm focus:outline-none bg-transparent" data-testid="user-products-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Barcode</th>
                  <th className="text-left px-4 py-3 font-semibold">Price</th>
                  <th className="text-left px-4 py-3 font-semibold">Qty</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-400">No products</td></tr>
                )}
                {filteredProducts.map(p => (
                  <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-product-row-${p.product_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{p.product_name}</div>
                      <div className="text-xs text-slate-400">{p.product_id}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs font-mono">{p.barcode || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">${(p.selling_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-slate-700">{p.quantity || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setEditingProduct({ ...p })} className="p-1.5 rounded-lg text-slate-500 hover:bg-[#0052FF]/10 hover:text-[#0052FF]" title="Edit" data-testid={`edit-product-${p.product_id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteProduct(p)} disabled={busy === p.product_id} className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40" title="Delete" data-testid={`delete-product-${p.product_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'batches' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Batch</th>
                  <th className="text-left px-4 py-3 font-semibold">Cost</th>
                  <th className="text-left px-4 py-3 font-semibold">Selling Total</th>
                  <th className="text-left px-4 py-3 font-semibold">Profit</th>
                  <th className="text-left px-4 py-3 font-semibold">Items</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-400">No batches</td></tr>
                )}
                {batches.map(b => (
                  <tr key={b.batch_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-batch-row-${b.batch_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{b.batch_name}</div>
                      <div className="text-xs text-slate-400">{b.batch_id}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">${parseFloat(b.cost_of_batch || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-slate-700">${(b.products_selling_total || 0).toFixed(2)}</td>
                    <td className={`px-4 py-3 font-semibold ${(b.projected_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${(b.projected_profit || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{b.product_count} ({b.total_units} units)</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setEditingBatch({ ...b })} className="p-1.5 rounded-lg text-slate-500 hover:bg-[#0052FF]/10 hover:text-[#0052FF]" title="Edit" data-testid={`edit-batch-${b.batch_id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteBatch(b)} disabled={busy === b.batch_id} className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40" title="Delete" data-testid={`delete-batch-${b.batch_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'integrations' && (
        <IntegrationsPanel
          data={integrations}
          loading={integrationsLoading}
          onRefresh={fetchIntegrations}
          username={username}
        />
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <Modal title="Edit Product" onClose={() => setEditingProduct(null)} testId="edit-product-modal">
          <Field label="Product Name" value={editingProduct.product_name} onChange={v => setEditingProduct(p => ({ ...p, product_name: v }))} />
          <Field label="Barcode / UPC" value={editingProduct.barcode || ''} onChange={v => setEditingProduct(p => ({ ...p, barcode: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Selling Price" type="number" value={editingProduct.selling_price ?? 0} onChange={v => setEditingProduct(p => ({ ...p, selling_price: v }))} />
            <Field label="Quantity" type="number" value={editingProduct.quantity ?? 0} onChange={v => setEditingProduct(p => ({ ...p, quantity: v }))} />
          </div>
          <Field label="Compare-at / Retail" value={editingProduct.retail_price || ''} onChange={v => setEditingProduct(p => ({ ...p, retail_price: v }))} />
          <ModalFooter onCancel={() => setEditingProduct(null)} onSave={saveProduct} busy={busy === 'save-product'} testId="save-edit-product" />
        </Modal>
      )}

      {/* Edit Batch Modal */}
      {editingBatch && (
        <Modal title="Edit Batch" onClose={() => setEditingBatch(null)} testId="edit-batch-modal">
          <Field label="Batch Name" value={editingBatch.batch_name} onChange={v => setEditingBatch(b => ({ ...b, batch_name: v }))} />
          <Field label="Notes" value={editingBatch.batch_notes || ''} onChange={v => setEditingBatch(b => ({ ...b, batch_notes: v }))} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost of Batch" type="number" value={editingBatch.cost_of_batch ?? 0} onChange={v => setEditingBatch(b => ({ ...b, cost_of_batch: v }))} />
            <Field label="Selling Price" type="number" value={editingBatch.selling_price ?? 0} onChange={v => setEditingBatch(b => ({ ...b, selling_price: v }))} />
          </div>
          <ModalFooter onCancel={() => setEditingBatch(null)} onSave={saveBatch} busy={busy === 'save-batch'} testId="save-edit-batch" />
        </Modal>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-semibold flex items-center gap-2 ${toast.kind === 'ok' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`} data-testid="admin-detail-toast">
          {toast.kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
    <div className="flex items-center gap-1.5 text-xs text-slate-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-sm font-semibold text-[#0F172A]">{value}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] focus:outline-none text-sm" />
  </div>
);

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid={testId}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h3 className="text-lg font-bold text-[#0F172A]">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-3">{children}</div>
    </div>
  </div>
);

const ModalFooter = ({ onCancel, onSave, busy, testId }) => (
  <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
    <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
    <button onClick={onSave} disabled={busy} data-testid={testId}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#0052FF] text-white hover:bg-[#0040CC] disabled:opacity-50">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
    </button>
  </div>
);

// ── Integrations Diagnostics ──────────────────────────────────────────────
const PROVIDER_META = {
  shopify: { label: 'Shopify', color: '#96bf48', icon: ShoppingBag,
    portalUrl: 'https://partners.shopify.com', portalLabel: 'Shopify Partners',
    fixHint: 'Add the customer\'s domain to "Allowed redirection URL(s)" in your Shopify Partners app config.' },
  square : { label: 'Square',  color: '#3E4348', icon: Store,
    portalUrl: 'https://developer.squareup.com/apps', portalLabel: 'Square Developer',
    fixHint: 'Add the redirect URL to your Square app\'s OAuth tab.' },
  ebay   : { label: 'eBay',    color: '#E53238', icon: Tag,
    portalUrl: 'https://developer.ebay.com/my/keys', portalLabel: 'eBay Developer',
    fixHint: 'Update "Your auth accepted URL" on your eBay RuName.' },
};

const IntegrationsPanel = ({ data, loading, onRefresh, username }) => {
  const [shopInput, setShopInput] = useState('');
  const [installResult, setInstallResult] = useState(null);
  const [installBusy, setInstallBusy] = useState(false);
  const [installError, setInstallError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateInstallLink = async () => {
    setInstallError(''); setInstallResult(null); setInstallBusy(true);
    try {
      const r = await apiCall(`/api/admin/users/${username}/shopify-install-link`, {
        method: 'POST', data: { shop: shopInput },
      });
      setInstallResult(r.data);
    } catch (e) {
      setInstallError(e.response?.data?.detail || e.message || 'Failed to generate link');
    } finally { setInstallBusy(false); }
  };

  const copyLink = () => {
    if (installResult?.install_url) {
      navigator.clipboard.writeText(installResult.install_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading && !data) return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF] mx-auto" />
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          OAuth tokens are masked. Hover for token preview.
        </div>
        <button onClick={onRefresh} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          data-testid="refresh-integrations">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Shopify install-link generator */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingBag className="w-4 h-4 text-[#96bf48]" />
          <h4 className="text-sm font-bold text-[#0F172A]">Send Shopify install link</h4>
          <span className="text-[10px] text-slate-400">— for Custom Distribution / Public apps</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Type the customer's myshopify domain. We'll generate a one-click install URL that connects their store directly to this user's account.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={shopInput} onChange={e => setShopInput(e.target.value)}
            placeholder="mystore   or   mystore.myshopify.com"
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0052FF] focus:outline-none text-sm"
            data-testid="shopify-install-shop-input" />
          <button onClick={generateInstallLink} disabled={!shopInput || installBusy}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#96bf48] text-white text-sm font-semibold hover:bg-[#7fa83c] disabled:opacity-50"
            data-testid="shopify-install-generate-btn">
            {installBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
            Generate link
          </button>
        </div>
        {installError && (
          <div className="mt-2 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-[11px]" data-testid="shopify-install-error">
            <AlertCircle className="w-3 h-3 inline -mt-0.5 mr-1" /> {installError}
          </div>
        )}
        {installResult && (
          <div className="mt-3 px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px]" data-testid="shopify-install-result">
            <div className="font-semibold text-emerald-800 mb-1.5">
              <CheckCircle2 className="w-3 h-3 inline -mt-0.5 mr-1" /> Install link ready for <span className="font-mono">{installResult.shop}.myshopify.com</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-emerald-200 rounded px-2 py-1.5 text-[10px] font-mono break-all max-h-16 overflow-hidden">
                {installResult.install_url}
              </code>
              <button onClick={copyLink} className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700"
                data-testid="shopify-install-copy-btn">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="mt-2 text-[10px] text-emerald-700">
              Email this URL to the customer. When they click it and approve, their store will appear as <strong>Connected</strong> below.
            </div>
          </div>
        )}
      </div>

      <ShopifyHmacSecretsPanel />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['shopify', 'square', 'ebay'].map(key => {
          const meta = PROVIDER_META[key];
          const d = data[key] || {};
          const ok = !!d.connected;
          const Icon = meta.icon;
          return (
            <div key={key} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`integration-card-${key}`}>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="inline-flex items-center gap-2">
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                  <span className="text-base font-bold text-[#0F172A]">{meta.label}</span>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                  data-testid={`integration-status-${key}`}>
                  {ok ? 'CONNECTED' : 'NOT CONNECTED'}
                </span>
              </div>
              <div className="p-4 space-y-2 text-xs">
                {ok ? (
                  <>
                    <Row label="Token" value={d.token_preview} mono />
                    {d.store && <Row label="Store" value={d.store} />}
                    {d.merchant_id && <Row label="Merchant" value={d.merchant_id} mono />}
                    {d.location_id && <Row label="Location" value={d.location_name || d.location_id} />}
                    {d.scopes && <Row label="Scopes" value={d.scopes} />}
                    {d.environment && <Row label="Env" value={d.environment} />}
                    {d.connected_at && <Row label="Connected" value={new Date(d.connected_at).toLocaleString()} />}
                  </>
                ) : (
                  <div className="text-sm text-slate-400 italic">No token saved.</div>
                )}

                {d.oauth_pending && (
                  <div className="mt-2 px-2.5 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-[11px]">
                    <div className="font-semibold mb-0.5">Pending OAuth</div>
                    The user clicked Connect but never completed the redirect. State row was created at <span className="font-mono">{d.oauth_pending.created_at}</span>.
                  </div>
                )}

                {d.last_error && (
                  <div className="mt-2 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-[11px]" data-testid={`integration-last-error-${key}`}>
                    <div className="font-semibold mb-0.5 inline-flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" /> Last error · <span className="font-mono">{d.last_error.code}</span>
                    </div>
                    <div className="text-[10px] text-red-700 mb-1">{new Date(d.last_error.at).toLocaleString()}</div>
                    {d.last_error.detail && <div className="font-mono text-[10px] break-all">{d.last_error.detail}</div>}
                    <div className="mt-1.5 pt-1.5 border-t border-red-200/70 text-[10px] text-red-700">
                      Likely fix: {meta.fixHint}{' '}
                      <a href={meta.portalUrl} target="_blank" rel="noreferrer" className="underline font-semibold">{meta.portalLabel} →</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start gap-2">
    <div className="w-20 flex-shrink-0 text-[10px] uppercase tracking-wider text-slate-400 font-semibold pt-0.5">{label}</div>
    <div className={`flex-1 text-slate-700 break-all ${mono ? 'font-mono text-[11px]' : ''}`}>{value || '—'}</div>
  </div>
);
// ── Shopify HMAC Secrets Manager (Global App Setting) ─────────────────────
// Lets admins paste each Shopify app's Client Secret here so the GDPR
// compliance webhooks can verify HMAC signatures from MULTIPLE Shopify apps
// (Custom Distribution + Public/App-Store) at once. Useful when the deployment
// platform doesn't allow adding new env vars on the fly.
const ShopifyHmacSecretsPanel = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/admin/shopify/hmac-secrets', { method: 'GET' });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr(''); setOkMsg('');
    try {
      const r = await apiCall('/api/admin/shopify/hmac-secrets', {
        method: 'PUT', data: { value: value.trim() },
      });
      setOkMsg(`Saved — ${r.data.db_count} DB secret(s) active`);
      setEditing(false); setValue('');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const totalActive = (data?.env_count || 0) + (data?.db_count || 0);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="hmac-secrets-panel">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-amber-500" />
        <h4 className="text-sm font-bold text-[#0F172A]">Shopify webhook HMAC secrets</h4>
        <span className="text-[10px] text-slate-400">— global app setting</span>
        <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${totalActive ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {totalActive} active
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Paste each Shopify app's Client Secret (comma-separated). The GDPR webhooks
        accept signatures from any listed secret, so multiple Shopify apps
        (Custom + Public/App-Store) can share this backend.
      </p>

      {/* Active secrets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">From environment</div>
          {(data?.env_masked || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic">none</div>
          ) : (
            <ul className="space-y-1">
              {data.env_masked.map((s, i) => (
                <li key={i} className="font-mono text-[11px] text-slate-700">{s}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">From database</div>
          {(data?.db_masked || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic">none</div>
          ) : (
            <ul className="space-y-1">
              {data.db_masked.map((s, i) => (
                <li key={i} className="font-mono text-[11px] text-slate-700">{s}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {!editing ? (
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50"
            data-testid="hmac-edit-btn">
            <Edit3 className="w-3.5 h-3.5" /> Edit DB secrets
          </button>
          {okMsg && <span className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{okMsg}</span>}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            <input type={show ? 'text' : 'password'} value={value} onChange={e => setValue(e.target.value)}
              placeholder="shpss_aaa…,shpss_bbb… (comma-separated, leave empty to clear)"
              className="w-full px-3 py-2 pr-10 rounded-lg border border-slate-200 focus:border-[#0052FF] focus:outline-none text-xs font-mono"
              data-testid="hmac-input" />
            <button type="button" onClick={() => setShow(!show)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0052FF] text-white text-xs font-semibold hover:bg-[#0040cc] disabled:opacity-50"
              data-testid="hmac-save-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
            <button onClick={() => { setEditing(false); setValue(''); setErr(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <span className="text-[10px] text-slate-400">Each secret must be 16+ chars.</span>
          </div>
        </div>
      )}

      {err && (
        <div className="mt-2 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-[11px]" data-testid="hmac-error">
          <AlertCircle className="w-3 h-3 inline -mt-0.5 mr-1" /> {err}
        </div>
      )}
    </div>
  );
};


